import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { purchaseItemService, SupplierPricingRowRequest } from '../../services/purchaseItemService';
import { productService } from '../../services/productService';
import { supplierService } from '../../services/supplierService';
import taxService, { Tax } from '../../services/taxService';
import { useCurrency } from '../../hooks/useCurrency';
import { parseApiError } from '../../utils/errorUtils';
import './PurchaseItemPage.css';
import { Save, ArrowLeft, Plus, X } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_LENGTHS = {
  supplierItemCode: 50,
  description: 200,
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'INR', 'LKR'];

// ─── Types ────────────────────────────────────────────────────────────────────

interface SupplierRow {
  _localId: string;
  supplierId: number;
  supplierItemCode: string;
  purchasePrice: number;
  currency: string;
  minimumOrderQuantity: number;
  leadTimeDays: number;
  description: string;
  taxId?: number;
}

interface RowErrors {
  supplierId?: string;
  purchasePrice?: string;
  minimumOrderQuantity?: string;
  leadTimeDays?: string;
  supplierItemCode?: string;
  description?: string;
}

interface FormErrors {
  productId?: string;
  suppliers?: string;
  rows?: Record<string, RowErrors>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const newRow = (defaultCurrency: string): SupplierRow => ({
  _localId: Math.random().toString(36).substring(2, 11),
  supplierId: 0,
  supplierItemCode: '',
  purchasePrice: 0,
  currency: defaultCurrency,
  minimumOrderQuantity: 0,
  leadTimeDays: 0,
  description: '',
  taxId: undefined,
});

// ─── Component ────────────────────────────────────────────────────────────────

const PurchaseItemCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedProductId = (location.state as { productId?: number } | null)?.productId;
  const { t } = useTranslation();
  const defaultCurrency = useCurrency();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});

  const [products, setProducts] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [productId, setProductId] = useState<number>(preselectedProductId || 0);
  const [rows, setRows] = useState<SupplierRow[]>([newRow(defaultCurrency)]);

  useEffect(() => {
    loadMasterData();
    taxService.getTaxes(1, 200).then(res => {
      const list: Tax[] = res?.data ?? [];
      setTaxes(list.filter(tx => tx.isActive && (tx.applicableFor === 'Purchase' || tx.applicableFor === 'Both')));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Reset first row to use new default currency when it changes
    setRows(prev => {
      if (prev.length === 1 && prev[0].purchasePrice === 0) {
        return [newRow(defaultCurrency)];
      }
      return prev;
    });
  }, [defaultCurrency]);

  const loadMasterData = async () => {
    try {
      const [prodRes, supRes] = await Promise.all([
        productService.getProducts({ pageSize: 1000 }),
        supplierService.getSuppliers({ pageSize: 1000 }),
      ]);
      if (prodRes.data.data) setProducts(prodRes.data.data);
      if (supRes.data.data)  setSuppliers(supRes.data.data);
    } catch {
      console.error(t('purchaseItem.failedToLoadItem'));
    }
  };

  // ── Row field change ────────────────────────────────────────────────────────

  const handleRowChange = useCallback(
    (localId: string, field: keyof SupplierRow, rawValue: string) => {
      setRows(prev =>
        prev.map(r => {
          if (r._localId !== localId) return r;

          let value: string | number = rawValue;

          if (field === 'supplierId') {
            value = parseInt(rawValue) || 0;
          } else if (field === 'taxId') {
            return { ...r, taxId: parseInt(rawValue) || undefined };
          } else if (field === 'purchasePrice') {
            value = parseFloat(rawValue) || 0;
          } else if (field === 'minimumOrderQuantity' || field === 'leadTimeDays') {
            value = parseInt(rawValue) ?? 0;
          } else if (field === 'supplierItemCode' && rawValue.length > MAX_LENGTHS.supplierItemCode) {
            value = rawValue.substring(0, MAX_LENGTHS.supplierItemCode);
          } else if (field === 'description' && rawValue.length > MAX_LENGTHS.description) {
            value = rawValue.substring(0, MAX_LENGTHS.description);
          }

          return { ...r, [field]: value };
        })
      );

      setFieldErrors(prev => ({
        ...prev,
        rows: {
          ...prev.rows,
          [localId]: { ...prev.rows?.[localId], [field]: undefined },
        },
      }));
    },
    []
  );

  const addRow = () => setRows(prev => [...prev, newRow(defaultCurrency)]);

  const removeRow = (localId: string) =>
    setRows(prev => prev.filter(r => r._localId !== localId));

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateForm = (): boolean => {
    const errors: FormErrors = { rows: {} };
    let isValid = true;

    if (!productId) {
      errors.productId = t('purchaseItem.formValidation.productRequired');
      isValid = false;
    }

    if (rows.length === 0) {
      errors.suppliers = t('purchaseItem.formValidation.atLeastOneSupplier');
      isValid = false;
    }

    const supplierCounts = rows.reduce<Record<number, number>>((acc, r) => {
      if (r.supplierId) acc[r.supplierId] = (acc[r.supplierId] ?? 0) + 1;
      return acc;
    }, {});
    if (Object.values(supplierCounts).some(c => c > 1)) {
      errors.suppliers = t('purchaseItem.formValidation.duplicateSupplier');
      isValid = false;
    }

    for (const row of rows) {
      const rowErr: RowErrors = {};

      if (!row.supplierId) {
        rowErr.supplierId = t('purchaseItem.formValidation.supplierRequired');
        isValid = false;
      }
      if (row.purchasePrice <= 0) {
        rowErr.purchasePrice = t('purchaseItem.formValidation.purchasePriceRequired');
        isValid = false;
      }
      if (row.minimumOrderQuantity < 0) {
        rowErr.minimumOrderQuantity = t('purchaseItem.formValidation.moqRequired');
        isValid = false;
      }
      if (row.leadTimeDays < 0) {
        rowErr.leadTimeDays = t('purchaseItem.formValidation.leadTimeInvalid');
        isValid = false;
      }
      if (row.supplierItemCode.length > MAX_LENGTHS.supplierItemCode) {
        rowErr.supplierItemCode = t('purchaseItem.formValidation.supplierItemCodeMaxLength');
        isValid = false;
      }
      if (row.description.length > MAX_LENGTHS.description) {
        rowErr.description = t('purchaseItem.formValidation.descriptionMaxLength');
        isValid = false;
      }

      if (Object.keys(rowErr).length > 0) {
        errors.rows![row._localId] = rowErr;
      }
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError =
        errors.productId ||
        errors.suppliers ||
        Object.values(errors.rows ?? {})
          .flatMap(re => Object.values(re))
          .find(e => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;

    setSaving(true);
    try {
      const supplierRows: SupplierPricingRowRequest[] = rows.map(r => ({
        supplierId: r.supplierId,
        supplierItemCode: r.supplierItemCode,
        purchasePrice: r.purchasePrice,
        currency: r.currency,
        minimumOrderQuantity: r.minimumOrderQuantity,
        leadTimeDays: r.leadTimeDays,
        description: r.description || undefined,
        taxId: r.taxId || undefined,
      }));

      await purchaseItemService.upsertProductSuppliers(productId, { suppliers: supplierRows });
      navigate('/purchase-items', {
        state: { message: t('purchaseItem.itemCreatedSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseItem.failedToCreateItem')).message);
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="pi-create-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate('/purchase-items')}
            >
              <ArrowLeft size={14} strokeWidth={2} />
              <span>{t('purchaseItem.backToList')}</span>
            </button>
            <h1 className="ims-page-title">{t('purchaseItem.createTitle')}</h1>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/purchase-items')}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? (
                <>
                  <span className="ims-btn-spinner" />
                  <span className="pi-btn-full">{t('common.saving')}</span>
                </>
              ) : (
                <>
                  <Save size={16} strokeWidth={1.75} />
                  {t('common.save')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* LEFT — Product Info ────────────────────────────────────────────── */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('purchaseItem.productInformation')}</h2>
              </div>
              <div className="ims-card-body">
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('purchaseItem.productName')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control${fieldErrors.productId ? ' is-invalid' : ''}`}
                    value={productId || ''}
                    onChange={e => {
                      setProductId(parseInt(e.target.value) || 0);
                      setFieldErrors(prev => ({ ...prev, productId: undefined }));
                      setError(null);
                    }}
                    disabled={!!preselectedProductId}
                  >
                    <option value="">{t('purchaseItem.selectProduct')}</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.sku} — {p.name}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.productId && (
                    <small className="pi-field-error">{fieldErrors.productId}</small>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CENTER — Supplier Pricing Table ────────────────────────────────── */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('purchaseItem.supplierPricing')}</h2>
                {fieldErrors.suppliers && (
                  <span className="pi-suppliers-error">{fieldErrors.suppliers}</span>
                )}
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  onClick={addRow}
                >
                  <Plus size={13} strokeWidth={2} />
                  {t('purchaseItem.addSupplier')}
                </button>
              </div>

              <div className="ims-lines-body">
                <div className="ims-table-scroll">
                  <table className="ims-table">
                    <thead>
                      <tr>
                        <th className="ims-th">
                          {t('purchaseItem.supplierId')} <span className="required">*</span>
                        </th>
                        <th className="ims-th ims-th--num">
                          {t('purchaseItem.purchasePrice')} <span className="required">*</span>
                        </th>
                        <th className="ims-th">
                          {t('purchaseItem.currency')} <span className="required">*</span>
                        </th>
                        <th className="ims-th ims-th--num">{t('purchaseItem.minimumOrderQuantity')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseItem.leadTimeDays')}</th>
                        <th className="ims-th">{t('purchaseItem.supplierItemCode')}</th>
                        <th className="ims-th">{t('purchaseItem.description')}</th>
                        <th className="ims-th">{t('purchaseItem.tax')}</th>
                        <th className="ims-th ims-th--del" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="ims-td pi-empty-row">
                            {t('purchaseItem.noSuppliersYet')}
                          </td>
                        </tr>
                      ) : (
                        rows.map(row => {
                          const rowErr = fieldErrors.rows?.[row._localId] ?? {};
                          return (
                            <tr key={row._localId} className="ims-tr">

                              {/* Supplier */}
                              <td className="ims-td">
                                <select
                                  className={`ims-cell-input${rowErr.supplierId ? ' is-invalid' : ''}`}
                                  value={row.supplierId || ''}
                                  onChange={e => handleRowChange(row._localId, 'supplierId', e.target.value)}
                                  title={rowErr.supplierId}
                                >
                                  <option value="">{t('purchaseItem.selectSupplier')}</option>
                                  {suppliers.map(s => (
                                    <option key={s.supplierId ?? s.id} value={s.supplierId ?? s.id}>
                                      {s.code} — {s.name}
                                    </option>
                                  ))}
                                </select>
                              </td>

                              {/* Purchase Price */}
                              <td className="ims-td ims-td--num">
                                <input
                                  type="number"
                                  className={`ims-cell-input ims-num-input${rowErr.purchasePrice ? ' is-invalid' : ''}`}
                                  value={row.purchasePrice || ''}
                                  onChange={e => handleRowChange(row._localId, 'purchasePrice', e.target.value)}
                                  placeholder="0.00"
                                  step="0.01"
                                  min="0.01"
                                  title={rowErr.purchasePrice}
                                />
                              </td>

                              {/* Currency */}
                              <td className="ims-td">
                                <select
                                  className="ims-cell-input"
                                  value={row.currency}
                                  onChange={e => handleRowChange(row._localId, 'currency', e.target.value)}
                                >
                                  {CURRENCIES.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                              </td>

                              {/* MOQ */}
                              <td className="ims-td ims-td--num">
                                <input
                                  type="number"
                                  className={`ims-cell-input ims-num-input${rowErr.minimumOrderQuantity ? ' is-invalid' : ''}`}
                                  value={row.minimumOrderQuantity}
                                  onChange={e => handleRowChange(row._localId, 'minimumOrderQuantity', e.target.value)}
                                  min="0"
                                  placeholder="0"
                                  title={rowErr.minimumOrderQuantity}
                                />
                              </td>

                              {/* Lead Time */}
                              <td className="ims-td ims-td--num">
                                <input
                                  type="number"
                                  className={`ims-cell-input ims-num-input${rowErr.leadTimeDays ? ' is-invalid' : ''}`}
                                  value={row.leadTimeDays}
                                  onChange={e => handleRowChange(row._localId, 'leadTimeDays', e.target.value)}
                                  min="0"
                                  placeholder="0"
                                  title={rowErr.leadTimeDays}
                                />
                              </td>

                              {/* Supplier Item Code */}
                              <td className="ims-td">
                                <input
                                  type="text"
                                  className={`ims-cell-input${rowErr.supplierItemCode ? ' is-invalid' : ''}`}
                                  value={row.supplierItemCode}
                                  onChange={e => handleRowChange(row._localId, 'supplierItemCode', e.target.value)}
                                  maxLength={MAX_LENGTHS.supplierItemCode}
                                  placeholder="e.g. SUP-001"
                                  title={rowErr.supplierItemCode}
                                />
                              </td>

                              {/* Description */}
                              <td className="ims-td">
                                <input
                                  type="text"
                                  className={`ims-cell-input${rowErr.description ? ' is-invalid' : ''}`}
                                  value={row.description}
                                  onChange={e => handleRowChange(row._localId, 'description', e.target.value)}
                                  maxLength={MAX_LENGTHS.description}
                                  placeholder={t('purchaseItem.description')}
                                  title={rowErr.description}
                                />
                              </td>

                              {/* Tax */}
                              <td className="ims-td">
                                <select
                                  className="ims-cell-input"
                                  value={row.taxId ?? ''}
                                  onChange={e => handleRowChange(row._localId, 'taxId', e.target.value)}
                                  title={t('purchaseItem.taxHint')}
                                >
                                  <option value="">{t('purchaseItem.noTax')}</option>
                                  {taxes.map(tax => (
                                    <option key={tax.taxId} value={tax.taxId}>
                                      {tax.taxName} ({tax.taxPercentage}%)
                                    </option>
                                  ))}
                                </select>
                              </td>

                              {/* Delete */}
                              <td className="ims-td ims-td--del">
                                <button
                                  type="button"
                                  className="ims-remove-btn"
                                  onClick={() => removeRow(row._localId)}
                                  title={t('purchaseItem.removeSupplier')}
                                >
                                  <X size={14} strokeWidth={2} />
                                </button>
                              </td>

                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Summary + Help ─────────────────────────────────────────── */}
          <div className="ims-panel-right">
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseItem.supplierPricing')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('purchaseItem.supplierId')}s</span>
                  <span className="ims-summary-value">{rows.length}</span>
                </div>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseItem.requiredFields')}</h3>
              </div>
              <div className="ims-card-body">
                <ul className="ims-help-list">
                  <li>{t('purchaseItem.productName')}</li>
                  <li>{t('purchaseItem.supplierId')}</li>
                  <li>{t('purchaseItem.purchasePrice')}</li>
                  <li>{t('purchaseItem.currency')}</li>
                </ul>
                <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                <p style={{ fontSize: 11, fontWeight: 600, color: '#374151', margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  {t('purchaseItem.optionalFields')}
                </p>
                <ul className="ims-help-list">
                  <li>{t('purchaseItem.minimumOrderQuantity')} (≥ 0)</li>
                  <li>{t('purchaseItem.leadTimeDays')} (≥ 0)</li>
                  <li>{t('purchaseItem.supplierItemCode')}</li>
                  <li>{t('purchaseItem.description')}</li>
                  <li>{t('purchaseItem.tax')}</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      </form>
    </Layout>
  );
};

export default PurchaseItemCreatePage;
